import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Row, Col, Statistic, Table, Tag, Button, Space, Form, Input, InputNumber, Select, DatePicker, Modal, message, Switch, Tabs } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  LockOutlined,
  AppstoreOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import { getActiveUsers, getDepartments, createUser, updateUser, deactivateUser, ContractType, Department, User, UserFormData, UserApiData, SupplierAccess } from '../services/userService';
import dayjs from 'dayjs';
import { getAllSuppliers, Supplier } from '../services/supplierApi';

const HR: React.FC = () => {
  const { Title, Text } = Typography;
  const { Option } = Select;
  // Interface UserFormData importée depuis userApi.ts
  // États
  const [employees, setEmployees] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [form] = Form.useForm<UserFormData>();
  const [editForm] = Form.useForm<UserFormData>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showLocationId, setShowLocationId] = useState(false);
  const [showEditLocationId, setShowEditLocationId] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [directoryTab, setDirectoryTab] = useState<'internal' | 'external'>('internal');
  
  const departmentById = useMemo(
    () => new Map(departments.map((dept) => [dept._id, dept])),
    [departments]
  );
  const supplierDepartmentId = useMemo(
    () => departments.find((dept) => dept.isSupplier)?._id,
    [departments]
  );
  const getDepartmentFlags = (departmentId?: string) =>
    departmentId ? departmentById.get(departmentId) : undefined;
  const supplierById = useMemo(
    () => new Map(suppliers.map((supplier) => [supplier._id || supplier.id, supplier])),
    [suppliers]
  );
  const isExternalUser = (user: User) =>
    Boolean(
      departmentById.get(user.departmentId || '')?.isSupplier ||
      (user.supplierAccesses && user.supplierAccesses.length > 0)
    );
  const displayedUsers = useMemo(
    () =>
      employees.filter((user) =>
        directoryTab === 'external' ? isExternalUser(user) : !isExternalUser(user)
      ),
    [directoryTab, employees, departmentById]
  );
  
  // Options d'accès pour le formulaire
  const accessOptions = [
    { label: 'Ressources Humaines', value: 'hr' },
    { label: 'Administration', value: 'admin' },
    { label: 'Shopify', value: 'shopify' },
    { label: 'Ressources', value: 'resources' },
    { label: 'Rapports', value: 'reporting' },
    { label: 'Approvisionnement', value: 'approvisionnement' },
    { label: 'Catalogue', value: 'catalog' },
    { label: 'Distributeurs', value: 'retailers' },
  ];

  // Charger les données
  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await getDepartments();
        setDepartments(data);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        message.error('Failed to load departments');
      }
    };

    fetchDepartments();
  }, []);
  
  // Charger les fournisseurs (scope reporting) sans appels Shopify pour éviter un prompt d'auth
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllSuppliers();
        setSuppliers(data);
      } catch (e) {
        console.error('Failed to load suppliers for reporting scope:', e);
      }
    })();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getActiveUsers();
      setEmployees(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      message.error('Failed to load employees');
      setLoading(false);
    }
  };

  // Afficher le modal d'ajout d'employé
  const showModal = () => {
    form.resetFields();
    setIsModalVisible(true);
  };
  
  // Fermer le modal d'ajout d'employé
  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };
  
  // Afficher le modal d'édition d'employé
  const showEditModal = (user: User) => {
    setCurrentUser(user);
    editForm.resetFields();
    // Préparer les données pour le formulaire
    const initialDepartmentId = user.departmentId;
    editForm.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      companyName: (user as any).companyName,
      supplierId: (user as any).supplierId,
      contract: user.contract,
      departmentId: initialDepartmentId,
      function: user.function,
      hourlyRate: (user as any).hourlyRate,
      monthlySalary: (user as any).monthlySalary,
      level: user.level,
      shopLocationId: user.shopLocationId,
      email: user.email,
      externalId: (user as any).externalId,
      accesses: user.accesses,
      supplierAccesses: user.supplierAccesses || [],
      isReportingAccess: Boolean(user.supplierAccesses && user.supplierAccesses.length > 0),
      supplierCatalogAccess: user.accesses?.includes('catalog'),
      // Pré-remplir le périmètre reporting s'il existe
      reportingScopeType: user.reportingScopeType || 'all',
      reportingSupplierId: user.reportingSupplierId || undefined,
      reportingBrands: user.reportingBrands || [],
      // Ne pas inclure le mot de passe pour l'édition
      // Convertir les dates si elles existent
      ...(user.startDate && { startDate: user.startDate ? dayjs(user.startDate) : undefined }),
      ...(user.endDate && { endDate: user.endDate ? dayjs(user.endDate) : undefined })
    });
    
    // Configurer l'affichage du champ shopLocationId si nécessaire
    const initialFlags = getDepartmentFlags(initialDepartmentId);
    setShowEditLocationId(Boolean(initialFlags?.isStore));
    
    setIsEditModalVisible(true);
  };
  
  // Fermer le modal d'édition d'employé
  const handleEditCancel = () => {
    editForm.resetFields();
    setIsEditModalVisible(false);
  };
  
  // Afficher le modal de confirmation de suppression
  const showDeleteModal = (user: User) => {
    setCurrentUser(user);
    setIsDeleteModalVisible(true);
  };
  
  // Fermer le modal de confirmation de suppression
  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (values: UserFormData) => {
    try {
      setSubmitLoading(true);
      
      // Convertir les dates Dayjs en strings pour l'API
      const userData: UserApiData = {
        ...values,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
        accesses: values.accesses || [], // S'assurer que les accès sont définis
        active: true
      };
      const isReportingAccess = Boolean(values.isReportingAccess);
      const departmentFlags = getDepartmentFlags(values.departmentId);
      
      // Si ce n'est pas une boutique, ne pas envoyer l'ID d'emplacement
      if (!departmentFlags?.isStore) {
        delete userData.shopLocationId;
      }

      if (isReportingAccess) {
        const supplier = supplierById.get(values.supplierId || '');
        const accessList = (values.supplierAccesses || []) as SupplierAccess[];
        if (!values.supplierId || !supplier) {
          message.error('Veuillez sélectionner un fournisseur valide');
          setSubmitLoading(false);
          return;
        }
        if (accessList.length === 0) {
          message.error('Veuillez ajouter au moins un accès fournisseur');
          setSubmitLoading(false);
          return;
        }
        userData.departmentId = values.departmentId;
        userData.supplierId = values.supplierId;
        userData.companyName = supplier.name;
        userData.accesses = [
          'reporting',
          ...(values.supplierCatalogAccess ? ['catalog' as const] : []),
        ];
        userData.reportingScopeType = 'supplier';
        userData.reportingSupplierId = values.supplierId;
        userData.reportingBrands = [];
        userData.supplierAccesses = accessList;
        delete (userData as any).firstName;
        delete (userData as any).lastName;
        delete (userData as any).contract;
        delete (userData as any).function;
        delete (userData as any).hourlyRate;
        delete (userData as any).monthlySalary;
        delete (userData as any).email;
        delete (userData as any).password;
        delete (userData as any).reportingScopeType;
        delete (userData as any).reportingSupplierId;
        delete (userData as any).reportingBrands;
      } else {
        delete (userData as any).companyName;
        delete (userData as any).supplierAccesses;
        const hasReporting = (userData.accesses || []).includes('reporting');
        if (hasReporting) {
          const scope = values.reportingScopeType || 'all';
          userData.reportingScopeType = scope;
          if (scope === 'supplier') {
            userData.reportingSupplierId = values.reportingSupplierId || undefined;
            userData.reportingBrands = [];
          } else if (scope === 'brands') {
            userData.reportingBrands = Array.isArray(values.reportingBrands) ? values.reportingBrands : [];
            userData.reportingSupplierId = null;
          } else {
            userData.reportingSupplierId = null;
            userData.reportingBrands = [];
          }
        } else {
          delete (userData as any).reportingScopeType;
          delete (userData as any).reportingSupplierId;
          delete (userData as any).reportingBrands;
        }
      }
      
      await createUser(userData);
      message.success('Employé ajouté avec succès');
      setIsModalVisible(false);
      fetchUsers(); // Recharger la liste
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Échec de l\'ajout de l\'employé';
      console.error('Erreur lors de l\'ajout d\'un employé:', error);
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Gérer la soumission du formulaire d'édition
  const handleEditSubmit = async (values: UserFormData) => {
    if (!currentUser?._id) return;
    
    try {
      setSubmitLoading(true);
      
      // Convertir les dates Dayjs en strings pour l'API
      const userData: Partial<User> = {
        ...values,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') as any : undefined,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') as any : undefined,
      };
      const isReportingAccess = Boolean(values.isReportingAccess);
      
      // Si un mot de passe vide est fourni, le supprimer de la requête
      if (!userData.password) {
        delete userData.password;
      }
      
      const departmentFlags = getDepartmentFlags(values.departmentId);

      // Si ce n'est pas une boutique, ne pas envoyer l'ID d'emplacement
      if (!departmentFlags?.isStore) {
        delete userData.shopLocationId;
      }

      // Normaliser les champs nom pour le département fournisseur (édition)
      if (isReportingAccess) {
        const supplier = supplierById.get(values.supplierId || currentUser?.supplierId || '');
        const accessList = (values.supplierAccesses || []) as SupplierAccess[];
        if (!supplier) {
          message.error('Veuillez sélectionner un fournisseur valide');
          setSubmitLoading(false);
          return;
        }
        if (accessList.length === 0) {
          message.error('Veuillez ajouter au moins un accès fournisseur');
          setSubmitLoading(false);
          return;
        }
        userData.supplierId = supplier._id || supplier.id;
        (userData as any).companyName = supplier.name;
        (userData as any).accesses = [
          'reporting',
          ...(values.supplierCatalogAccess ? ['catalog'] : []),
        ];
        (userData as any).reportingScopeType = 'supplier';
        (userData as any).reportingSupplierId = userData.supplierId;
        (userData as any).reportingBrands = [];
        (userData as any).supplierAccesses = accessList;
        delete (userData as any).firstName;
        delete (userData as any).lastName;
        delete (userData as any).contract;
        delete (userData as any).function;
        delete (userData as any).hourlyRate;
        delete (userData as any).monthlySalary;
        delete (userData as any).email;
        delete (userData as any).password;
      } else {
        delete (userData as any).companyName;
        delete (userData as any).supplierAccesses;
        const hasReportingEdit = (userData.accesses || []).includes('reporting');
        if (hasReportingEdit) {
          const scope = values.reportingScopeType || 'all';
          (userData as any).reportingScopeType = scope;
          if (scope === 'supplier') {
            (userData as any).reportingSupplierId = values.reportingSupplierId || currentUser.reportingSupplierId || undefined;
            (userData as any).reportingBrands = [];
          } else if (scope === 'brands') {
            (userData as any).reportingBrands = Array.isArray(values.reportingBrands)
              ? values.reportingBrands
              : currentUser.reportingBrands || [];
            (userData as any).reportingSupplierId = null;
          } else {
            (userData as any).reportingSupplierId = null;
            (userData as any).reportingBrands = [];
          }
        } else {
          delete (userData as any).reportingScopeType;
          delete (userData as any).reportingSupplierId;
          delete (userData as any).reportingBrands;
        }
      }
      
      await updateUser(currentUser._id, userData);
      message.success('Employé mis à jour avec succès');
      setIsEditModalVisible(false);
      fetchUsers(); // Recharger la liste
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Échec de la mise à jour de l\'employé';
      console.error('Erreur lors de la mise à jour d\'un employé:', error);
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Gérer la désactivation d'un utilisateur
  const handleDeleteUser = async () => {
    if (!currentUser?._id) return;
    
    try {
      setSubmitLoading(true);
      
      await deactivateUser(currentUser._id);
      message.success('Employé désactivé avec succès');
      setIsDeleteModalVisible(false);
      fetchUsers(); // Recharger la liste
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Échec de la désactivation de l\'employé';
      console.error('Erreur lors de la désactivation d\'un employé:', error);
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Définition des colonnes du tableau avec un design "startup"
  const columns = [
    {
      title: 'Collaborateur',
      key: 'name',
      render: (record: User) => {
        const isSupplier = departmentById.get(record.departmentId || '')?.isSupplier;
        const supplierEmail = record.email || '—';
        const accessCount = record.supplierAccesses?.length || 0;
        return (
          <Space size="middle" align="start">
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '12px', 
              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              color: '#0f172a',
              fontWeight: 700,
              fontSize: '14px',
              border: '1px solid #fff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              {isSupplier
                ? (record.companyName || 'SU').substring(0, 2).toUpperCase()
                : `${record.firstName?.[0] ?? ''}${record.lastName?.[0] ?? ''}`.toUpperCase()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Text strong style={{ fontSize: '14px', color: '#0f172a', lineHeight: '1.2' }}>
                {isSupplier
                  ? record.companyName
                  : `${record.firstName ?? ''} ${record.lastName ?? ''}`}
              </Text>
              {isSupplier ? (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {accessCount} accès fournisseur • {supplierEmail}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Poste & Département',
      key: 'function_dept',
      render: (record: User) => {
        const isSupplier = departmentById.get(record.departmentId || '')?.isSupplier;
        if (!isSupplier) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text style={{ fontSize: '13px', color: '#334155' }}>{record.function || '—'}</Text>
              <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {departmentById.get(record.departmentId || '')?.label || '—'}
              </Text>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(record.supplierAccesses || []).map((access, index) => (
              <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ fontSize: '12px' }}>{access.firstName} {access.lastName} • {access.function}</Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>{access.email}</Text>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Contrat',
      dataIndex: 'contract',
      key: 'contract',
      render: (contract?: string) => {
        if (!contract) return <Text type="secondary">—</Text>;
        let color = '#64748b';
        let bg = '#f1f5f9';
        
        switch (contract) {
          case ContractType.EMPLOYEE: color = '#10b981'; bg = '#ecfdf5'; break;
          case ContractType.FREELANCE: color = '#3b82f6'; bg = '#eff6ff'; break;
          case ContractType.INTERIM: color = '#8b5cf6'; bg = '#f5f3ff'; break;
          case ContractType.INTERNSHIP: color = '#f59e0b'; bg = '#fffbeb'; break;
          case ContractType.APPRENTICESHIP: color = '#06b6d4'; bg = '#ecfeff'; break;
        }
        
        return (
          <Tag bordered={false} style={{ background: bg, color: color, fontWeight: 600, borderRadius: '6px', fontSize: '11px' }}>
            {contract.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Ancienneté',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text style={{ fontSize: '13px' }}>{date ? dayjs(date).format('DD MMM YYYY') : '—'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {date ? `Arrivé il y a ${dayjs().diff(dayjs(date), 'month')} mois` : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Accès',
      dataIndex: 'accesses',
      key: 'accesses',
      render: (accesses: string[]) => (
        <div style={{ maxWidth: '200px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {accesses && accesses.length > 0 ? (
            accesses.map(access => {
              let label = access.toUpperCase();
              if (access === 'approvisionnement') label = 'SUPPLY';
              if (access === 'retailers') label = 'DISTRIB';
              
              return (
                <span key={access} style={{ 
                  fontSize: '10px', 
                  padding: '2px 6px', 
                  background: '#f8fafc', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '4px',
                  color: '#64748b',
                  fontWeight: 500
                }}>
                  {label}
                </span>
              );
            })
          ) : (
            <Text type="secondary" style={{ fontSize: '11px' }}>Aucun accès</Text>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right' as const,
      render: (_: unknown, record: User) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EditOutlined style={{ color: '#64748b' }} />} 
            onClick={() => showEditModal(record)}
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => showDeleteModal(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px', background: '#f8fafc', minHeight: '100vh' }}>
      <style>{`
        .neo-main-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        .neo-main-tabs .ant-tabs-tab {
          padding: 12px 16px !important;
          font-weight: 600 !important;
        }
        .neo-main-tabs .ant-tabs-tab-active {
          color: #0f172a !important;
        }
        .neo-main-tabs .ant-tabs-tab-btn:hover {
          color: #0f172a70 !important;
        }
        .neo-main-tabs .ant-tabs-tab-btn {
          color: #0f172a70 !important;
        }
        .neo-main-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0f172a !important;
        }
        .neo-main-tabs .ant-tabs-ink-bar {
          background: #0f172a !important;
          height: 3px !important;
        }
      `}</style>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Ressources Humaines
          </Title>
          <Text type="secondary" style={{ fontSize: '16px', color: '#64748b' }}>
            Gestion des talents, effectifs et orchestration des accès.
          </Text>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={showModal}
          style={{ 
            height: '44px', 
            borderRadius: '10px', 
            fontWeight: 600, 
            background: '#0f172a', 
            borderColor: '#0f172a',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          Nouvel Employé
        </Button>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title={<Text strong style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Effectif Total</Text>}
              value={employees.length}
              prefix={<TeamOutlined style={{ color: '#0f172a', marginRight: '8px' }} />}
              valueStyle={{ fontWeight: 800, fontSize: '28px', color: '#0f172a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
              Actifs sur la plateforme
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title={<Text strong style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Salariés CDI/CDD</Text>}
              value={employees.filter(e => e.contract === ContractType.EMPLOYEE).length}
              prefix={<UserOutlined style={{ color: '#3b82f6', marginRight: '8px' }} />}
              valueStyle={{ fontWeight: 800, fontSize: '28px', color: '#0f172a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
              Contrats permanents
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title={<Text strong style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Apprenants</Text>}
              value={
                employees.filter(
                  e => e.contract === ContractType.INTERNSHIP || 
                       e.contract === ContractType.APPRENTICESHIP
                ).length
              }
              prefix={<CalendarOutlined style={{ color: '#f59e0b', marginRight: '8px' }} />}
              valueStyle={{ fontWeight: 800, fontSize: '28px', color: '#0f172a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
              Stages & Alternances
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title={<Text strong style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Externes</Text>}
              value={
                employees.filter(
                  e => e.contract === ContractType.FREELANCE || 
                       e.contract === ContractType.INTERIM
                ).length
              }
              prefix={<FileDoneOutlined style={{ color: '#8b5cf6', marginRight: '8px' }} />}
              valueStyle={{ fontWeight: 800, fontSize: '28px', color: '#0f172a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
              Freelance & Intérim
            </div>
          </Card>
        </Col>
      </Row>

      <Card 
        bordered={false}
        style={{ 
          borderRadius: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
          overflow: 'hidden'
        }}
        styles={{ body: { padding: '0' }, header: { borderBottom: '1px solid #f1f5f9', padding: '20px 24px' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '10px' }}>
              <TeamOutlined style={{ color: '#0f172a' }} />
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Répertoire du Personnel</div>
              <div style={{ fontSize: '12px', fontWeight: 400, color: '#64748b' }}>Liste exhaustive des collaborateurs actifs</div>
            </div>
          </div>
        }
      >
        <Tabs
          activeKey={directoryTab}
          onChange={(key) => setDirectoryTab(key as 'internal' | 'external')}
          className="neo-main-tabs"
          style={{ padding: '16px 24px 0' }}
          items={[
            {
              key: 'internal',
              label: `Utilisateurs internes (${employees.filter((user) => !isExternalUser(user)).length})`,
            },
            {
              key: 'external',
              label: `Accès externes (${employees.filter(isExternalUser).length})`,
            },
          ]}
        />
        <Table 
          columns={columns} 
          dataSource={displayedUsers}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            style: { padding: '16px 24px' }
          }}
          className="modern-table"
        />
      </Card>
      
      {/* Modal pour ajouter un employé */}
      <Modal
        title={
          <div style={{ padding: '8px 0' }}>
            <Title level={4} style={{ margin: 0 }}>Ajouter un nouveau collaborateur</Title>
            <Text type="secondary">Complétez les informations pour créer l'accès.</Text>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        width={640}
        centered
        footer={[
          <Button key="cancel" onClick={handleCancel} style={{ borderRadius: '8px', height: '40px' }}>
            Annuler
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={submitLoading} 
            onClick={() => form.submit()}
            style={{ borderRadius: '8px', height: '40px', background: '#0f172a', borderColor: '#0f172a' }}
          >
            Créer le profil
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: '24px' }}
          requiredMark={false}
        >
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserOutlined /> Informations Identitaires
            </Title>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}>
              {() => {
                const depId = form.getFieldValue('departmentId');
                if (depId) return null;
                return (
                  <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                    Sélectionnez d'abord un département pour afficher le formulaire fournisseur.
                  </Text>
                );
              }}
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}
            >
              {() => {
                const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
                if (isReportingAccess) {
                  return (
                    <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                      Les accès fournisseurs sont définis dans la section “Permissions & Accès”.
                    </Text>
                  );
                }
                return (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="firstName"
                        label={<Text strong style={{ fontSize: '13px' }}>Prénom</Text>}
                        rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
                      >
                        <Input size="large" style={{ borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="lastName"
                        label={<Text strong style={{ fontSize: '13px' }}>Nom</Text>}
                        rules={[{ required: true, message: 'Veuillez entrer le nom' }]}
                      >
                        <Input size="large" style={{ borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
            
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
              {() => {
                const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
                if (isReportingAccess) return null;
                return (
                  <>
                    <Form.Item
                      name="email"
                      label={<Text strong style={{ fontSize: '13px' }}>Email Professionnel</Text>}
                      rules={[
                        { required: true, message: "Veuillez entrer l'email" },
                        { type: 'email', message: 'Veuillez entrer un email valide' }
                      ]}
                    >
                      <Input size="large" placeholder="collaborateur@honnore.com" style={{ borderRadius: '8px' }} />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label={<Text strong style={{ fontSize: '13px' }}>Mot de passe initial</Text>}
                      rules={[{ required: true, message: 'Veuillez entrer un mot de passe' }]}
                    >
                      <Input.Password size="large" prefix={<LockOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: '8px' }} />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
          </div>
          
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
            {() => {
              const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
              if (isReportingAccess) return null;
              return (
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                  <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AppstoreOutlined /> Poste & Rémunération
                  </Title>
                  <Form.Item
                    name="departmentId"
                    label={<Text strong style={{ fontSize: '13px' }}>Département</Text>}
                    rules={[{ required: true, message: 'Veuillez sélectionner un département' }]}
                  >
                    <Select 
                      size="large"
                      placeholder="Choisir..."
                      style={{ borderRadius: '8px' }}
                      onChange={(value) => {
                        const departmentFlags = getDepartmentFlags(value as string);
                        setShowLocationId(Boolean(departmentFlags?.isStore));
                        if (!departmentFlags?.isStore) {
                          form.setFieldValue('shopLocationId', undefined);
                        }
                        if (departmentFlags?.isSupplier) {
                          form.setFieldsValue({ firstName: undefined, lastName: undefined, contract: undefined, function: undefined });
                        } else {
                          form.setFieldsValue({ companyName: undefined, supplierId: undefined });
                        }
                      }}
                    >
                      {departments.map(dept => (
                        <Option key={dept._id} value={dept._id}>{dept.label || dept.key}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}>
                    {() => {
                      const depId = form.getFieldValue('departmentId');
                      if (getDepartmentFlags(depId)?.isSupplier) return null;
                      return (
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="function"
                              label={<Text strong style={{ fontSize: '13px' }}>Fonction</Text>}
                              rules={[{ required: true, message: 'Veuillez entrer une fonction' }]}
                            >
                              <Input size="large" style={{ borderRadius: '8px' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="contract"
                              label={<Text strong style={{ fontSize: '13px' }}>Type de contrat</Text>}
                              rules={[{ required: true, message: 'Veuillez sélectionner un type de contrat' }]}
                            >
                              <Select size="large" placeholder="Choisir..." style={{ borderRadius: '8px' }}>
                                {Object.values(ContractType).map(type => (
                                  <Option key={type} value={type}>{type}</Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                    }}
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId || prev.contract !== cur.contract}
                  >
                    {() => {
                      const depId = form.getFieldValue('departmentId');
                      if (getDepartmentFlags(depId)?.isSupplier) return null;
                      const contract = form.getFieldValue('contract');
                      const isHourly = contract === ContractType.FREELANCE || contract === ContractType.INTERIM;
                      return isHourly ? (
                        <Form.Item
                          name="hourlyRate"
                          label={<Text strong style={{ fontSize: '13px' }}>Taux horaire (€/h)</Text>}
                          rules={[{ required: true, message: 'Veuillez renseigner un taux horaire' }]}
                        >
                          <InputNumber size="large" style={{ width: '100%', borderRadius: '8px' }} min={0} step={0.5} />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="monthlySalary"
                          label={<Text strong style={{ fontSize: '13px' }}>Salaire mensuel (€/mois)</Text>}
                          rules={[{ required: true, message: 'Veuillez renseigner un salaire mensuel' }]}
                        >
                          <InputNumber size="large" style={{ width: '100%', borderRadius: '8px' }} min={0} step={50} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  {showLocationId && (
                    <Form.Item
                      name="shopLocationId"
                      label={<Text strong style={{ fontSize: '13px' }}>ID d'emplacement Shopify</Text>}
                      rules={[
                        { required: true, message: "L'ID d'emplacement est requis pour les employés de boutique" },
                        { pattern: /^gid:\/\/shopify\/Location\/\d+$/, message: "Format invalide. Exemple: gid://shopify/Location/90099974478" }
                      ]}
                    >
                      <Input size="large" prefix={<ShopOutlined style={{ color: '#94a3b8' }} />} placeholder="gid://shopify/Location/..." style={{ borderRadius: '8px' }} />
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.Item>
          
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LockOutlined /> Permissions & Accès
            </Title>
            <Form.Item shouldUpdate={(prev, cur) => prev.accesses !== cur.accesses || prev.departmentId !== cur.departmentId || prev.isReportingAccess !== cur.isReportingAccess} noStyle>
              {() => {
                const depId = form.getFieldValue('departmentId');
                const isSupplier = getDepartmentFlags(depId)?.isSupplier;
                const accesses = form.getFieldValue('accesses') || [];
                const hasReporting = accesses.includes('reporting');
                const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
                return (
                  <Form.Item label={<Text strong style={{ fontSize: '13px' }}>Accès reporting</Text>}>
                    <Switch
                      checked={Boolean(isSupplier || hasReporting || isReportingAccess)}
                      disabled={Boolean(isSupplier)}
                      onChange={(checked) => {
                        const next = checked
                          ? Array.from(new Set([...accesses, 'reporting']))
                          : accesses.filter((a: string) => a !== 'reporting');
                        form.setFieldsValue({
                          accesses: next,
                          isReportingAccess: checked,
                          departmentId: checked ? supplierDepartmentId || form.getFieldValue('departmentId') : form.getFieldValue('departmentId'),
                          reportingScopeType: checked ? form.getFieldValue('reportingScopeType') || 'all' : undefined,
                          reportingSupplierId: checked ? form.getFieldValue('reportingSupplierId') : undefined,
                          reportingBrands: checked ? form.getFieldValue('reportingBrands') || [] : [],
                        });
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item name="isReportingAccess" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}
            >
              {() =>
                getDepartmentFlags(form.getFieldValue('departmentId'))?.isSupplier ? (
                  <Form.Item
                    name="supplierCatalogAccess"
                    label={<Text strong style={{ fontSize: '13px' }}>Accès catalogue fournisseur</Text>}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId || prev.isReportingAccess !== cur.isReportingAccess}
              noStyle
            >
              {() => {
                const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
                if (isReportingAccess) return null;
                const depId = form.getFieldValue('departmentId');
                const isSupplier = getDepartmentFlags(depId)?.isSupplier;
                return (
                  <Form.Item
                    name="accesses"
                    label={<Text strong style={{ fontSize: '13px' }}>Modules autorisés</Text>}
                    initialValue={['resources']}
                    rules={[{ required: true, message: 'Veuillez sélectionner au moins un accès' }]}
                  >
                    <Select
                      mode="multiple"
                      size="large"
                      placeholder="Donner des accès..."
                      options={isSupplier ? [{ label: 'Rapports', value: 'reporting' }] : accessOptions}
                      style={{ borderRadius: '8px' }}
                      prefix={<AppstoreOutlined />}
                      disabled={Boolean(isSupplier)}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess || prev.supplierId !== cur.supplierId}
              noStyle
            >
              {() => {
                const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
                if (!isReportingAccess) return null;
                return (
                  <div style={{ background: '#fff', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '12px' }}>
                    <Form.Item
                      name="supplierId"
                      label={<Text strong style={{ fontSize: '13px' }}>Fournisseur</Text>}
                      rules={[{ required: true, message: 'Veuillez sélectionner un fournisseur' }]}
                    >
                      <Select
                        showSearch
                        size="large"
                        placeholder="Sélectionner un fournisseur"
                        optionFilterProp="label"
                        style={{ borderRadius: '8px' }}
                        options={suppliers.map((supplier) => ({
                          label: supplier.name,
                          value: supplier._id || supplier.id,
                        }))}
                      />
                    </Form.Item>

                    <Form.List name="supplierAccesses">
                      {(fields, { add, remove }) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <Text strong style={{ fontSize: '13px' }}>Accès fournisseurs</Text>
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
                              <Row gutter={12}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'firstName']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Prénom</Text>}
                                    rules={[{ required: true, message: 'Prénom requis' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'lastName']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Nom</Text>}
                                    rules={[{ required: true, message: 'Nom requis' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'email']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Email</Text>}
                                    rules={[{ required: true, message: 'Email requis' }, { type: 'email', message: 'Email invalide' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'password']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Mot de passe</Text>}
                                    rules={[{ required: true, message: 'Mot de passe requis' }]}
                                  >
                                    <Input.Password size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={24}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'function']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Fonction</Text>}
                                    rules={[{ required: true, message: 'Fonction requise' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Button danger onClick={() => remove(name)} icon={<DeleteOutlined />}>
                                Retirer l'accès
                              </Button>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                            Ajouter un accès
                          </Button>
                        </div>
                      )}
                    </Form.List>
                  </div>
                );
              }}
            </Form.Item>
          </div>
          
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
            {() => {
              const isReportingAccess = Boolean(form.getFieldValue('isReportingAccess'));
              if (isReportingAccess) return null;
              return (
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px' }}>
                  <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined /> Dates & Temporalité
                  </Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="startDate"
                        label={<Text strong style={{ fontSize: '13px' }}>Date d'entrée</Text>}
                      >
                        <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} placeholder="Début" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="endDate"
                        label={<Text strong style={{ fontSize: '13px' }}>Date de sortie prévue</Text>}
                      >
                        <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} placeholder="Fin (optionnel)" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour éditer un employé */}
      <Modal
        title={
          <div style={{ padding: '8px 0' }}>
            <Title level={4} style={{ margin: 0 }}>Modifier le profil</Title>
            <Text type="secondary">Mise à jour des informations de {currentUser?.firstName} {currentUser?.lastName}.</Text>
          </div>
        }
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        width={640}
        centered
        footer={[
          <Button key="cancel" onClick={handleEditCancel} style={{ borderRadius: '8px', height: '40px' }}>
            Annuler
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={submitLoading} 
            onClick={() => editForm.submit()}
            style={{ borderRadius: '8px', height: '40px', background: '#0f172a', borderColor: '#0f172a' }}
          >
            Sauvegarder les modifications
          </Button>
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
          style={{ marginTop: '24px' }}
          requiredMark={false}
        >
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserOutlined /> Informations Identitaires
            </Title>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}
            >
              {() => {
                const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
                if (isReportingAccess) {
                  return (
                    <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                      Les accès fournisseurs sont définis dans la section “Permissions & Accès”.
                    </Text>
                  );
                }
                return (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="firstName"
                        label={<Text strong style={{ fontSize: '13px' }}>Prénom</Text>}
                        rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
                      >
                        <Input size="large" style={{ borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="lastName"
                        label={<Text strong style={{ fontSize: '13px' }}>Nom</Text>}
                        rules={[{ required: true, message: 'Veuillez entrer le nom' }]}
                      >
                        <Input size="large" style={{ borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
            
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
              {() => {
                const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
                if (isReportingAccess) return null;
                return (
                  <>
                    <Form.Item
                      name="email"
                      label={<Text strong style={{ fontSize: '13px' }}>Email Professionnel</Text>}
                      rules={[
                        { required: true, message: "Veuillez entrer l'email" },
                        { type: 'email', message: 'Veuillez entrer un email valide' }
                      ]}
                    >
                      <Input size="large" style={{ borderRadius: '8px' }} />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label={<Text strong style={{ fontSize: '13px' }}>Modifier le mot de passe (laisser vide pour ne pas changer)</Text>}
                    >
                      <Input.Password size="large" prefix={<LockOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: '8px' }} />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
          </div>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
            {() => {
              const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
              if (isReportingAccess) return null;
              return (
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                  <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AppstoreOutlined /> Poste & Rémunération
                  </Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="departmentId"
                        label={<Text strong style={{ fontSize: '13px' }}>Département</Text>}
                        rules={[{ required: true, message: 'Veuillez sélectionner un département' }]}
                      >
                        <Select 
                          size="large"
                          placeholder="Choisir..."
                          style={{ borderRadius: '8px' }}
                          onChange={(value) => {
                            const departmentFlags = getDepartmentFlags(value as string);
                            setShowEditLocationId(Boolean(departmentFlags?.isStore));
                            if (!departmentFlags?.isStore) {
                              editForm.setFieldValue('shopLocationId', undefined);
                            }
                            if (departmentFlags?.isSupplier) {
                              editForm.setFieldsValue({ firstName: undefined, lastName: undefined, contract: undefined, function: undefined });
                            } else {
                              editForm.setFieldsValue({ companyName: undefined });
                            }
                          }}
                        >
                          {departments.map(dept => (
                            <Option key={dept._id} value={dept._id}>{dept.label || dept.key}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}>
                        {() => {
                          const depId = editForm.getFieldValue('departmentId');
                          if (getDepartmentFlags(depId)?.isSupplier) {
                            return null;
                          }
                          return (
                            <Form.Item
                              name="function"
                              label={<Text strong style={{ fontSize: '13px' }}>Fonction</Text>}
                              rules={[{ required: true, message: 'Veuillez entrer une fonction' }]}
                            >
                              <Input size="large" style={{ borderRadius: '8px' }} />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}>
                    {() => {
                      const depId = editForm.getFieldValue('departmentId');
                      if (getDepartmentFlags(depId)?.isSupplier) {
                        return null;
                      }
                      return (
                        <Form.Item
                          name="contract"
                          label={<Text strong style={{ fontSize: '13px' }}>Type de contrat</Text>}
                          rules={[{ required: true, message: 'Veuillez sélectionner un type de contrat' }]}
                        >
                          <Select size="large" placeholder="Choisir..." style={{ borderRadius: '8px' }}>
                            {Object.values(ContractType).map(type => (
                              <Option key={type} value={type}>{type}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId || prev.contract !== cur.contract}
                  >
                    {() => {
                      const depId = editForm.getFieldValue('departmentId');
                      if (getDepartmentFlags(depId)?.isSupplier) return null;
                      const contract = editForm.getFieldValue('contract');
                      const isHourly = contract === ContractType.FREELANCE || contract === ContractType.INTERIM;
                      return isHourly ? (
                        <Form.Item
                          name="hourlyRate"
                          label={<Text strong style={{ fontSize: '13px' }}>Taux horaire (€/h)</Text>}
                          rules={[{ required: true, message: 'Veuillez renseigner un taux horaire' }]}
                        >
                          <InputNumber size="large" style={{ width: '100%', borderRadius: '8px' }} min={0} step={0.5} />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="monthlySalary"
                          label={<Text strong style={{ fontSize: '13px' }}>Salaire mensuel (€/mois)</Text>}
                          rules={[{ required: true, message: 'Veuillez renseigner un salaire mensuel' }]}
                        >
                          <InputNumber size="large" style={{ width: '100%', borderRadius: '8px' }} min={0} step={50} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  
                  {showEditLocationId && (
                    <Form.Item
                      name="shopLocationId"
                      label={<Text strong style={{ fontSize: '13px' }}>ID d'emplacement Shopify</Text>}
                      rules={[
                        { required: true, message: "L'ID d'emplacement est requis pour les employés de boutique" },
                        { pattern: /^gid:\/\/shopify\/Location\/\d+$/, message: "Format invalide. Exemple: gid://shopify/Location/90099974478" }
                      ]}
                    >
                      <Input size="large" prefix={<ShopOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: '8px' }} />
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.Item>

          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LockOutlined /> Permissions & Accès
            </Title>
            <Form.Item shouldUpdate={(prev, cur) => prev.accesses !== cur.accesses || prev.departmentId !== cur.departmentId || prev.isReportingAccess !== cur.isReportingAccess} noStyle>
              {() => {
                const depId = editForm.getFieldValue('departmentId');
                const isSupplier = getDepartmentFlags(depId)?.isSupplier;
                const accesses = editForm.getFieldValue('accesses') || [];
                const hasReporting = accesses.includes('reporting');
                const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
                return (
                  <Form.Item label={<Text strong style={{ fontSize: '13px' }}>Accès reporting</Text>}>
                    <Switch
                      checked={Boolean(isSupplier || hasReporting || isReportingAccess)}
                      disabled={Boolean(isSupplier)}
                      onChange={(checked) => {
                        const next = checked
                          ? Array.from(new Set([...accesses, 'reporting']))
                          : accesses.filter((a: string) => a !== 'reporting');
                        editForm.setFieldsValue({
                          accesses: next,
                          isReportingAccess: checked,
                          departmentId: checked ? supplierDepartmentId || editForm.getFieldValue('departmentId') : editForm.getFieldValue('departmentId'),
                          reportingScopeType: checked ? editForm.getFieldValue('reportingScopeType') || 'all' : undefined,
                          reportingSupplierId: checked ? editForm.getFieldValue('reportingSupplierId') : undefined,
                          reportingBrands: checked ? editForm.getFieldValue('reportingBrands') || [] : [],
                        });
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item name="isReportingAccess" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.departmentId !== cur.departmentId}
            >
              {() =>
                getDepartmentFlags(editForm.getFieldValue('departmentId'))?.isSupplier ? (
                  <Form.Item
                    name="supplierCatalogAccess"
                    label={<Text strong style={{ fontSize: '13px' }}>Accès catalogue fournisseur</Text>}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}
              noStyle
            >
              {() => {
                const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
                if (isReportingAccess) return null;
                const depId = editForm.getFieldValue('departmentId');
                const isSupplier = getDepartmentFlags(depId)?.isSupplier;
                return (
                  <Form.Item
                    name="accesses"
                    label={<Text strong style={{ fontSize: '13px' }}>Modules autorisés</Text>}
                    rules={[{ required: true, message: 'Veuillez sélectionner au moins un accès' }]}
                  >
                    <Select
                      mode="multiple"
                      size="large"
                      placeholder="Donner des accès..."
                      options={isSupplier ? [{ label: 'Rapports', value: 'reporting' }] : accessOptions}
                      style={{ borderRadius: '8px' }}
                      prefix={<AppstoreOutlined />}
                      disabled={Boolean(isSupplier)}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess || prev.supplierId !== cur.supplierId}
              noStyle
            >
              {() => {
                const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
                if (!isReportingAccess) return null;
                return (
                  <div style={{ background: '#fff', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '12px' }}>
                    <Form.Item
                      name="supplierId"
                      label={<Text strong style={{ fontSize: '13px' }}>Fournisseur</Text>}
                      rules={[{ required: true, message: 'Veuillez sélectionner un fournisseur' }]}
                    >
                      <Select
                        showSearch
                        size="large"
                        placeholder="Sélectionner un fournisseur"
                        optionFilterProp="label"
                        style={{ borderRadius: '8px' }}
                        options={suppliers.map((supplier) => ({
                          label: supplier.name,
                          value: supplier._id || supplier.id,
                        }))}
                      />
                    </Form.Item>

                    <Form.List name="supplierAccesses">
                      {(fields, { add, remove }) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <Text strong style={{ fontSize: '13px' }}>Accès fournisseurs</Text>
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
                              <Row gutter={12}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'firstName']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Prénom</Text>}
                                    rules={[{ required: true, message: 'Prénom requis' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'lastName']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Nom</Text>}
                                    rules={[{ required: true, message: 'Nom requis' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'email']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Email</Text>}
                                    rules={[{ required: true, message: 'Email requis' }, { type: 'email', message: 'Email invalide' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'password']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Nouveau mot de passe</Text>}
                                    rules={[
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const access = getFieldValue(['supplierAccesses', name]);
                                          if (value || access?._id) return Promise.resolve();
                                          return Promise.reject(new Error('Mot de passe requis pour un nouvel accès'));
                                        },
                                      }),
                                    ]}
                                  >
                                    <Input.Password
                                      size="large"
                                      placeholder="Laisser vide pour conserver le mot de passe"
                                      style={{ borderRadius: '8px' }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={24}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'function']}
                                    label={<Text strong style={{ fontSize: '12px' }}>Fonction</Text>}
                                    rules={[{ required: true, message: 'Fonction requise' }]}
                                  >
                                    <Input size="large" style={{ borderRadius: '8px' }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Button danger onClick={() => remove(name)} icon={<DeleteOutlined />}>
                                Retirer l'accès
                              </Button>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                            Ajouter un accès
                          </Button>
                        </div>
                      )}
                    </Form.List>
                  </div>
                );
              }}
            </Form.Item>
          </div>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isReportingAccess !== cur.isReportingAccess}>
            {() => {
              const isReportingAccess = Boolean(editForm.getFieldValue('isReportingAccess'));
              if (isReportingAccess) return null;
              return (
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px' }}>
                  <Title level={5} style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined /> Dates & Temporalité
                  </Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="startDate"
                        label={<Text strong style={{ fontSize: '13px' }}>Date d'entrée</Text>}
                      >
                        <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="endDate"
                        label={<Text strong style={{ fontSize: '13px' }}>Date de sortie prévue</Text>}
                      >
                        <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de confirmation de désactivation */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ExclamationCircleFilled style={{ color: '#ef4444', fontSize: '22px' }} />
            <span style={{ fontSize: '18px', fontWeight: 700 }}>Désactiver le compte</span>
          </div>
        }
        open={isDeleteModalVisible}
        onOk={handleDeleteUser}
        onCancel={handleDeleteCancel}
        confirmLoading={submitLoading}
        okText="Désactiver définitivement"
        cancelText="Conserver"
        okButtonProps={{ danger: true, style: { borderRadius: '8px', height: '40px' } }}
        cancelButtonProps={{ style: { borderRadius: '8px', height: '40px' } }}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <Text style={{ fontSize: '15px' }}>
            Êtes-vous sûr de vouloir désactiver <strong>{currentUser?.firstName} {currentUser?.lastName}</strong> ?
          </Text>
          <div style={{ marginTop: '12px', background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #fee2e2' }}>
            <Text type="danger" style={{ fontSize: '13px' }}>
              Cette action retirera immédiatement tous les accès à la plateforme. Les données historiques seront conservées mais l'utilisateur ne pourra plus se connecter.
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HR;
